/**
 * Canned Steam responses.
 *
 * ARCHITECTURE §5.6: "`fetchLive`/`enrich`/`giFlush` hit `/api/steam/*` for real
 * when `wrangler dev` is up, and return canned fixtures from
 * `app/src/data/fixtures/` when it is not — so a Steam outage never blocks UI
 * work."
 *
 * These are *shapes*, not a mirror of live Steam: enough rows to exercise the
 * live-search branch of `matchLine` (`· N LIVE VIA STEAM`), the discount and
 * demo ribbons, and the art ladder's `assets` rung.
 */

import type { AssetMap, Game, SteamFactPatch } from '../../lib/types.ts';
import type { BrowseQuery, SteamTag } from '../../repo/Repository.ts';

const liveRow = (over: Partial<Game> & Pick<Game, 'id' | 'appid' | 'n'>): Game => ({
  dev: '—',
  rd: '',
  p: null,
  o: 0,
  mn: 0,
  mx: 0,
  plat: 'P',
  tags: [],
  rp: 0,
  pk: 0,
  ccu: 0,
  up: false,
  ds: 'Live Steam search result — open it and full details load from the store page.',
  ea: false,
  demo: false,
  dAid: 0,
  art: '',
  live: true,
  thumb: '',
  ...over,
});

/**
 * Fixture rows keyed by the normalized query prefix they answer. `fetchLive`
 * falls back to `FIXTURE_LIVE_DEFAULT` for anything else so the offline path
 * always has *something* to render.
 */
export const FIXTURE_LIVE: Readonly<Record<string, Game[]>> = {
  wolves: [
    liveRow({ id: 1818140, appid: 1818140, n: 'Den of Wolves', dev: '10 Chambers', up: true }),
  ],
  star: [
    liveRow({ id: 3837830, appid: 3837830, n: 'StarRupture', dev: 'Creepy Jar', up: true }),
    liveRow({ id: 2820740, appid: 2820740, n: 'Prologue: Go Wayback', dev: 'PlayerUnknown Productions', up: true }),
  ],
};

export const FIXTURE_LIVE_DEFAULT: readonly Game[] = [
  liveRow({ id: 1818140, appid: 1818140, n: 'Den of Wolves', dev: '10 Chambers', up: true }),
];

/** `enrich()` patches — the volatile set only (INVENTORY §5.3). */
export const FIXTURE_FACTS: Readonly<Record<string, SteamFactPatch>> = {
  '548430': { appid: 548430, p: 9.89, o: 29.99, up: false, ea: false, demo: false, dAid: 0, rd: '2020-05', rp: 97 },
  '3883570': { appid: 3883570, p: null, o: 0, up: true, ea: false, demo: true, dAid: 4704030, rd: 'TBA', rp: 93 },
  '1962700': { appid: 1962700, p: 29.99, o: 0, up: false, ea: true, demo: false, dAid: 0, rd: '2026-05', rp: 93 },
};

/** `giFlush()` art records. */
export const FIXTURE_ASSETS: AssetMap = {
  '3883570': {
    lib: 'https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/3883570/2f47e8044af8208d7c5c6c91a865f7b84941d316/library_capsule.jpg',
    hdr: 'https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/3883570/2f47e8044af8208d7c5c6c91a865f7b84941d316/header.jpg',
  },
};

export function fixtureLiveFor(q: string): Game[] {
  const nq = q.toLowerCase();
  for (const key of Object.keys(FIXTURE_LIVE)) {
    if (nq.includes(key)) return (FIXTURE_LIVE[key] ?? []).map((g) => ({ ...g }));
  }
  return FIXTURE_LIVE_DEFAULT.map((g) => ({ ...g }));
}

// ---------------------------------------------------------------------------
// browse — offline parity for Discover's Steam search (docs/DISCOVERY.md §2.2)
// ---------------------------------------------------------------------------

/**
 * Real Steam tag ids, so an offline run exercises the same ids production does.
 * 1685 Co-op and 1667 Horror are the two measured in `docs/DISCOVERY.md`;
 * 1100689 is included because it is the id that proves these are NOT smallints.
 */
export const FIXTURE_TAGS: SteamTag[] = [
  { id: 1685, name: 'Co-op' },
  { id: 1667, name: 'Horror' },
  { id: 19, name: 'Action' },
  { id: 21, name: 'Adventure' },
  { id: 9, name: 'Strategy' },
  { id: 122, name: 'RPG' },
  { id: 492, name: 'Indie' },
  { id: 1100689, name: 'Open World Survival Craft' },
];

/**
 * What STEAM knows about a fixture row, as opposed to what the row carries.
 *
 * These four are the keys Steam's own search sorts and narrows on, and they are
 * held BESIDE the game rather than on it — exactly as `tags` is — because a
 * browse row deliberately arrives priceless. DISCOVERY §6 keeps the volatile set
 * off a page cached for ten minutes, so `toBrowseGame` hard-codes `p: null,
 * o: 0` and a fixture that emitted prices would be a laxer shape than production
 * ever produces, quietly excusing client code from handling the real one.
 *
 * Steam holds this data and answers with it; our row does not. Modelling the
 * split is the whole point of the fixture.
 */
interface BrowseTraits {
  /** Store price in dollars. `null` = no price (unreleased). */
  p: number | null;
  /** Pre-discount price; `0` = not on sale. */
  o: number;
  /** `YYYY-MM`. `''` = no date Steam can sort on, which `Released_DESC` drops. */
  rd: string;
  /** Review percent — the `Reviews_DESC` key. `0` = too few reviews to rank. */
  rp: number;
}

/** Browse rows carry Steam's tag ids so the offline path can filter by them. */
const browseRow = (
  over: Partial<Game> & Pick<Game, 'id' | 'appid' | 'n'>,
  tags: readonly number[],
  traits: BrowseTraits,
): { game: Game; tags: readonly number[]; traits: BrowseTraits } => ({
  // `rs: 8` beside `rp: 90` because that is what a real browse row carries:
  // Steam sends the rung in the same `summary_filtered` block as the
  // percentage. It is also what lets the offline path exercise the twin swap's
  // rung carry — a 90% row's rung cannot be derived from 90 alone.
  game: liveRow({ dev: 'Fixture Studio', rp: 90, rs: 8, ...over }),
  tags,
  traits,
});

// `pcats` values are the real Steam player-category ids (`lib/format.ts` has
// the table): 1 Multi-player · 2 Single-player · 9 Co-op · 24 Split Screen ·
// 36 Online PvP · 38 Online Co-op. They ride the rows exactly as `toBrowseGame`
// carries them off a real page, which is what lets the offline build exercise
// the crew lens's positive-knowledge arm and `playersTxt`'s CO-OP/MULTI/SOLO
// fallback instead of rendering an em dash on every tile.
const FIXTURE_BROWSE: ReadonlyArray<{ game: Game; tags: readonly number[]; traits: BrowseTraits }> = [
  browseRow({ id: 4704690, appid: 4704690, n: 'MECCHA CHAMELEON', pcats: [1, 9, 38] }, [1685, 19], { p: 5.99, o: 0, rd: '2026-06', rp: 84 }),
  browseRow({ id: 381210, appid: 381210, n: 'Dead by Daylight', pcats: [1, 36] }, [1685, 1667], { p: 4.99, o: 19.99, rd: '2016-06', rp: 82 }),
  browseRow({ id: 1144200, appid: 1144200, n: 'Ready or Not', pcats: [1, 9, 38] }, [1685, 19], { p: 39.99, o: 0, rd: '2023-12', rp: 88 }),
  browseRow({ id: 892970, appid: 892970, n: 'Valheim', pcats: [1, 9, 38] }, [1685, 1100689], { p: 19.99, o: 0, rd: '2021-02', rp: 95 }),
  browseRow({ id: 105600, appid: 105600, n: 'Terraria', pcats: [1, 9, 24, 38] }, [1685, 21], { p: 4.99, o: 9.99, rd: '2011-05', rp: 97 }),
  browseRow({ id: 1086940, appid: 1086940, n: "Baldur's Gate 3", pcats: [2, 1, 9, 24, 38] }, [1685, 122], { p: 59.99, o: 0, rd: '2023-08', rp: 96 }),
  browseRow({ id: 1966720, appid: 1966720, n: 'Lethal Company', ea: true, pcats: [1, 38] }, [1685, 1667], { p: 9.99, o: 0, rd: '2023-10', rp: 98 }),
  browseRow({ id: 322330, appid: 322330, n: "Don't Starve Together", pcats: [1, 9, 38] }, [1685, 1100689], { p: 3.74, o: 14.99, rd: '2016-04', rp: 96 }),
  browseRow({ id: 739630, appid: 739630, n: 'Phasmophobia', pcats: [1, 9, 38] }, [1685, 1667], { p: 13.99, o: 0, rd: '2020-09', rp: 96 }),
  browseRow({ id: 570, appid: 570, n: 'Dota 2', pcats: [1, 36] }, [9, 19], { p: 0, o: 0, rd: '2013-07', rp: 81 }),
  browseRow({ id: 1245620, appid: 1245620, n: 'ELDEN RING', pcats: [2, 1, 9] }, [19, 21], { p: 59.99, o: 0, rd: '2022-02', rp: 93 }),
  // `up: false` because the SEEDED CATALOG says appid 4000 is released, and the
  // catalog twin swap (`slices/browse.ts`) replaces this row with that one on
  // every offline browse. A fixture that disagreed with the catalog about the
  // same appid made UPCOMING ONLY answer with nothing at all — the fixture
  // claimed one thing, the row that actually rendered said another.
  browseRow({ id: 4000, appid: 4000, n: "Garry's Mod", pcats: [1, 9, 24] }, [1685, 492], { p: 9.99, o: 0, rd: '2006-11', rp: 95 }),
  // THE UNRELEASED ROW, and it is deliberately an appid the catalog does NOT
  // hold: only a row that survives the twin swap can demonstrate `comingsoon`
  // and the UPCOMING tri-state offline. No price and no date, so it is also the
  // row `Price_ASC` and `Released_DESC` each have to place or drop on purpose.
  browseRow({ id: 1818140, appid: 1818140, n: 'Den of Wolves', dev: '10 Chambers', up: true, pcats: [1, 9, 38] }, [1685, 19], { p: null, o: 0, rd: '', rp: 0 }),
  // THE SINGLE-PLAYER-ONLY ROW — `pcats: [2]` and nothing else — reachable by
  // term ("witcher") and not in the seeded catalog, so it survives the twin
  // swap. It exists for the crew lens's one answerable browse-mode claim:
  // Steam positively saying "none of you can play this together".
  browseRow({ id: 292030, appid: 292030, n: 'The Witcher 3: Wild Hunt', dev: 'CD PROJEKT RED', pcats: [2] }, [122, 21], { p: 39.99, o: 0, rd: '2015-05', rp: 95 }),
];

/**
 * The offline answer for a browse query: tags are ANDed and the term is a
 * substring of the name, which is the same shape Steam applies. Deliberately
 * NOT paged — an offline run has twelve rows, so page 2 is legitimately empty
 * and that is a state the UI needs to handle anyway.
 *
 * IT SORTS AND NARROWS, because Steam does. This fixture is the only seam a
 * `VITE_REPOSITORY=local` run and the store tests have on this route, so a
 * version that ignored `sort` would make the offline build reproduce, exactly,
 * the defect the upstream forwarding exists to fix — a sort control that moves
 * nothing. The fixture rows carry real prices and release flags for the same
 * reason: `specials` and `comingsoon` have to be answerable here too.
 */
/**
 * What `player-lookup` would answer for the fixture browse's THIN rows — the
 * ones the seeded catalog does not hold, whose tiles therefore render with the
 * 0 seat sentinel until the live-once lookup lands. Keyed by appid string, the
 * same shape `FIXTURE_FACTS` uses. Witcher (solo-only) is deliberately absent:
 * the client never asks about a row its own category check ruled solo, and an
 * absent answer is what an ask would return anyway.
 */
export const FIXTURE_SEATS: Readonly<Record<string, { appid: number; mn: number; mx: number; unb: boolean }>> = {
  '4704690': { appid: 4704690, mn: 1, mx: 4, unb: false },
  '381210': { appid: 381210, mn: 1, mx: 5, unb: false },
  '1144200': { appid: 1144200, mn: 1, mx: 8, unb: false },
  '739630': { appid: 739630, mn: 1, mx: 4, unb: false },
  '1818140': { appid: 1818140, mn: 1, mx: 4, unb: false },
};

export function fixtureBrowseFor(q: BrowseQuery): Game[] {
  if (q.start > 0) return [];
  const nq = q.term.trim().toLowerCase();
  const hits = FIXTURE_BROWSE.filter(
    (r) =>
      q.tags.every((t) => r.tags.includes(t)) &&
      (nq === '' || r.game.n.toLowerCase().includes(nq)),
  );

  // Steam narrows before it sorts. A cap of `'free'` means exactly $0 —
  // `maxprice=free` is its own upstream value, not `maxprice=0`.
  const cap = q.maxPrice === null ? null : q.maxPrice === 'free' ? 0 : q.maxPrice;
  let out = hits.filter(({ game, traits: t }) => {
    if (cap !== null && (t.p === null || t.p > cap)) return false;
    if (q.specials && !(t.o > 0 && t.p !== null && t.o > t.p)) return false;
    // `filter=comingsoon` is Steam's own release-state answer, and the row's
    // `up` flag is the fixture's copy of it — the one narrowing key that lives
    // on the game rather than in the traits, because the client sees it too.
    if (q.comingSoon && !game.up) return false;
    return true;
  });

  // Two of Steam's sorts also DROP the rows that cannot answer the key —
  // measured, 20,689 → 15,838 for `Released_DESC` and → 6,634 for
  // `Reviews_DESC`. Mirrored here so an offline run reproduces the surprising
  // half of the behaviour (the sort changes the TOTAL) and not just the order.
  const px = (p: number | null): number => (p === null ? Number.POSITIVE_INFINITY : p);
  if (q.sort === 'Released_DESC') out = out.filter((r) => r.traits.rd !== '');
  if (q.sort === 'Reviews_DESC') out = out.filter((r) => r.traits.rp > 0);
  const sorted = [...out];
  switch (q.sort) {
    case 'Price_ASC':
      sorted.sort((a, b) => px(a.traits.p) - px(b.traits.p) || a.game.appid - b.game.appid);
      break;
    case 'Price_DESC':
      sorted.sort((a, b) => px(b.traits.p) - px(a.traits.p) || a.game.appid - b.game.appid);
      break;
    case 'Name_ASC':
      sorted.sort((a, b) => (a.game.n < b.game.n ? -1 : a.game.n > b.game.n ? 1 : 0));
      break;
    case 'Released_DESC':
      sorted.sort((a, b) => (a.traits.rd < b.traits.rd ? 1 : a.traits.rd > b.traits.rd ? -1 : 0));
      break;
    case 'Reviews_DESC':
      sorted.sort((a, b) => b.traits.rp - a.traits.rp || a.game.appid - b.game.appid);
      break;
    default:
      // Relevance: the fixture's declaration order, standing in for Steam's
      // popularity-shaped default.
      break;
  }

  // `tids` rides along exactly as `toBrowseGame` would carry it off a real page,
  // so the offline path exercises the client-side exclude too. `stOk` likewise:
  // these rows answer their release state, and the status tri-states have to see
  // that here as well. The TRAITS do not ride along — a browse row is priceless
  // and stays that way.
  return sorted.map((r) => ({ ...r.game, tids: [...r.tags], stOk: true as const }));
}
